import { useShallow } from 'zustand/shallow';

import { CustomFilterCards } from './CustomFilterCards';
import { CustomTransferFilterPicker } from './CustomTransferFilterPicker';
import { CustomTransferSuggestions } from './CustomTransferSuggestions';
import {
  buildFilterSnapshot,
  useCustomTransfersStore
} from '@lib/transfers/store/useCustomTransfersStore';
import Button from '@components/atoms/button';
import { BodyRegular, H3 } from '@components/atoms/typography';
import { usePlannerState } from '@hooks/usePlannerState';
import { useLeagueContext } from '@hooks/useLeagueContext';

export const CustomTransfers = () => {
  const { context } = useLeagueContext();
  const { data: plannerData } = usePlannerState();

  const nextGameweek = context?.nextGameweek?.number;
  const gameweekState = plannerData?.data.gameweeks.find(
    (gameweek) => gameweek.gameweek === nextGameweek
  );
  const squad = [
    ...(gameweekState?.starting ?? []),
    ...(gameweekState?.bench ?? [])
  ];

  const {
    layoutOrder,
    noOfTransfers,
    effectiveOwnership,
    playerPrice,
    playerPosition,
    teamIds,
    playerInItems,
    playerOutIds,
    showCustomSuggestions,
    appliedFilters,
    applyFilters
  } = useCustomTransfersStore(
    useShallow((state) => ({
      layoutOrder: state.layoutOrder,
      noOfTransfers: state.noOfTransfers,
      effectiveOwnership: state.effectiveOwnership,
      playerPrice: state.playerPrice,
      playerPosition: state.playerPosition,
      teamIds: state.teamIds,
      playerInItems: state.playerInItems,
      playerOutIds: state.playerOutIds,
      showCustomSuggestions: state.showCustomSuggestions,
      appliedFilters: state.appliedFilters,
      applyFilters: state.applyFilters
    }))
  );

  const hasFilters = layoutOrder.length > 0;

  // The filters stay mounted alongside the suggestions, but the query only
  // runs against the applied snapshot: tweaking a filter card never fetches
  // by itself. Once the drafts drift from the snapshot the button returns as
  // "Update suggestions" so the refetch is always user-initiated.
  const draftFilters = buildFilterSnapshot({
    layoutOrder,
    noOfTransfers,
    effectiveOwnership,
    playerPrice,
    playerPosition,
    teamIds,
    playerInItems,
    playerOutIds
  });
  const hasUnappliedChanges =
    showCustomSuggestions &&
    appliedFilters !== null &&
    JSON.stringify(draftFilters) !== JSON.stringify(appliedFilters);

  const isSuggestionsVisible = showCustomSuggestions && hasFilters;
  const isApplyButtonVisible =
    hasFilters && (!showCustomSuggestions || hasUnappliedChanges);

  return (
    <div className="flex flex-col gap-4" data-cy="custom-transfers-view">
      <div className="flex flex-col gap-2">
        <H3 className="mb-0">Custom transfers</H3>
        <BodyRegular className="mb-0">
          Adjust the AI transfer recommendations based on your own filters.
        </BodyRegular>
      </div>

      <div>
        <CustomTransferFilterPicker />
      </div>

      <CustomFilterCards squad={squad} />

      {isApplyButtonVisible && (
        <div>
          <Button
            variant="primary"
            onClick={applyFilters}
            dataCy="suggest-custom-transfers"
          >
            {showCustomSuggestions
              ? 'Update suggestions'
              : 'Suggest transfers based on filters'}
          </Button>
        </div>
      )}

      {isSuggestionsVisible && <CustomTransferSuggestions />}
    </div>
  );
};
